import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => String(currentYear - 3 + i));

export default function CommunityExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterCat, setFilterCat] = useState("all");
  const [form, setForm] = useState({ category: "", description: "", amount: "", date: "" });

  const load = useCallback(async () => {
    try {
      let params = {};
      if (filterYear !== "all") params.year = parseInt(filterYear);
      if (filterCat !== "all") params.category = filterCat;
      const [eRes, cRes] = await Promise.all([
        axios.get(`${API}/community/expenses`, { params }),
        axios.get(`${API}/community/expense-categories`),
      ]);
      setExpenses(eRes.data); setCategories(cRes.data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando"); }
    finally { setLoading(false); }
  }, [filterYear, filterCat]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ category: "", description: "", amount: "", date: new Date().toISOString().split('T')[0] }); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ category: e.category, description: e.description, amount: String(e.amount), date: e.date }); setDialogOpen(true); };

  const handleSave = async () => {
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (editing) { await axios.put(`${API}/community/expenses/${editing.id}`, payload); toast.success("Gasto actualizado"); }
      else { await axios.post(`${API}/community/expenses`, payload); toast.success("Gasto registrado"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try { await axios.delete(`${API}/community/expenses/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const total = expenses.reduce((s, e) => s + e.amount, 0);
  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="community-expenses-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Gastos Comunidad</h2>
          <p className="text-sm text-gray-500 mt-1">{expenses.length} gastos | Total: {formatCurrency(total)}</p>
        </div>
        <button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2" data-testid="add-expense-button">
          <Plus className="w-4 h-4" /> Nuevo gasto
        </button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <Select value={filterYear} onValueChange={v => { setFilterYear(v); setLoading(true); }}><SelectTrigger className="w-28 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select>
        <Select value={filterCat} onValueChange={v => { setFilterCat(v); setLoading(true); }}><SelectTrigger className="w-44 rounded-sm"><SelectValue placeholder="Categoria" /></SelectTrigger><SelectContent><SelectItem value="all">Todas</SelectItem>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm">
        <Table>
          <TableHeader><TableRow className="bg-gray-50">
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Fecha</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Categoria</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Descripcion</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Importe</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {expenses.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No hay gastos</TableCell></TableRow>
            ) : expenses.map(e => (
              <TableRow key={e.id} className="hover:bg-gray-50">
                <TableCell className="text-sm text-gray-600 py-3 px-4">{e.date}</TableCell>
                <TableCell className="py-3 px-4"><span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">{e.category}</span></TableCell>
                <TableCell className="text-sm text-gray-900 py-3 px-4">{e.description}</TableCell>
                <TableCell className="text-sm font-medium text-red-600 py-3 px-4 text-right">{formatCurrency(e.amount)}</TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(e)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(e.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar gasto" : "Nuevo gasto"}</DialogTitle><DialogDescription>Registra un gasto de la comunidad</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger className="rounded-sm" data-testid="expense-category-select"><SelectValue placeholder="Selecciona" /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Descripcion</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Detalle del gasto" className="rounded-sm" data-testid="expense-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Importe</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-sm" data-testid="expense-amount-input" /></div>
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-sm" data-testid="expense-date-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all">Cancelar</button>
            <button onClick={handleSave} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="expense-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
